import { useEffect, useState } from 'react';

/** Запас в пикселях, с которым снимается признак переполнения */
const OVERFLOW_HYSTERESIS = 4;

/** Результат работы хука useContentOverflow */
type ContentOverflowType<T extends HTMLElement> = {
	/** Ref на скроллящуюся область - сколько места есть */
	contentRef: (node: T | null) => void;
	/** Ref на обёртку содержимого - сколько места нужно */
	innerRef: (node: HTMLElement | null) => void;
	/** Содержимое не помещается в область, то есть она реально скроллится */
	isOverflowed: boolean;
};

// Отслеживает, скроллится ли содержимое области. Нужен, чтобы прижатый снизу
// тулбар мог показать усиленную рамку и тень вверх, когда контент уезжает под него,
// и оставался плоским, когда контент помещается целиком
const useContentOverflow = <
	T extends HTMLElement = HTMLDivElement,
>(): ContentOverflowType<T> => {
	const [areaNode, setAreaNode] = useState<T | null>(null);
	const [innerNode, setInnerNode] = useState<HTMLElement | null>(null);
	const [isOverflowed, setIsOverflowed] = useState<boolean>(false);

	// Синхронизация с DOM: следим за размерами области и содержимого
	useEffect(() => {
		if (!areaNode || !innerNode) {
			return;
		}

		let isOverflowedNow = false;
		const checkOverflow = () => {
			const available = areaNode.clientHeight;
			const needed = innerNode.offsetHeight;
			// Снимаем признак с запасом
			isOverflowedNow = isOverflowedNow
				? needed > available - OVERFLOW_HYSTERESIS
				: needed > available;
			setIsOverflowed(isOverflowedNow);
		};

		const observer = new ResizeObserver(checkOverflow);
		observer.observe(areaNode);
		observer.observe(innerNode);
		checkOverflow();

		return () => {
			return observer.disconnect();
		};
	}, [areaNode, innerNode]);

	return {
		contentRef: setAreaNode,
		innerRef: setInnerNode,
		isOverflowed,
	};
};

export default useContentOverflow;
